// ─── Character ────────────────────────────────────────────────────────────────

import {
  Direction,
  CHARACTER_WIDTH,
  CHARACTER_HEIGHT,
  WALK_ANIM_FRAMES,
  WALK_ANIM_SPEED,
  TILE_SIZE,
  type Position,
} from "./constants";
import type { Camera } from "./camera";
import type { SpriteSheet } from "./sprite-sheet";

type CharState = "IDLE" | "WANDER" | "PATROL" | "WALKING";

export interface PatrolPath {
  x: number;
  y: number;
  pauseMs: number;
}

export class Character {
  position: Position;
  direction: Direction;
  spriteKey: string;
  color: string;
  animFrame: number = 0;
  isMoving: boolean = false;

  private animTick: number = 0;
  private width: number = CHARACTER_WIDTH;
  private height: number = CHARACTER_HEIGHT;
  private state: CharState = "IDLE";
  private stateTimer: number = 0;
  private wanderTarget: Position | null = null;
  private charId: string = "";
  private patrolPath: PatrolPath[] = [];
  private patrolIndex: number = 0;
  private pauseTimer: number = 0;
  facingPlayer: boolean = false;

  constructor(
    x: number,
    y: number,
    spriteKey: string,
    color: string = "#E74C3C",
    direction: Direction = Direction.Down,
    charId: string = "",
    patrolPath: PatrolPath[] = [],
  ) {
    this.position = { x, y };
    this.spriteKey = spriteKey;
    this.color = color;
    this.direction = direction;
    this.charId = charId;
    this.patrolPath = patrolPath;
  }

  /**
   * Update walk animation state + wander AI for NPCs.
   */
  update(dt: number, collisionGrid?: boolean[][], _pathfinder?: unknown): void {
    if (this.isMoving) {
      this.animTick++;
      if (this.animTick >= WALK_ANIM_SPEED) {
        this.animTick = 0;
        this.animFrame = (this.animFrame + 1) % WALK_ANIM_FRAMES;
      }
    } else {
      this.animFrame = 0;
      this.animTick = 0;
    }

    // NPC wander AI — only if this is an NPC (has charId)
    if (!this.charId) return;

    this.stateTimer += dt;

    // Patrol mode — follows explicit waypoints instead of random wander
    if (this.patrolPath.length > 0) {
      if (this.pauseTimer > 0) {
        this.pauseTimer -= dt;
        this.isMoving = false;
        this.state = "IDLE";
      } else {
        const waypoint = this.patrolPath[this.patrolIndex % this.patrolPath.length];
        const dx = waypoint.x - this.position.x;
        const dy = waypoint.y - this.position.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 5) {
          this.isMoving = true;
          this.state = "PATROL";
          const speed = 1.0;
          this.position.x += (dx / dist) * speed;
          this.position.y += (dy / dist) * speed;
          if (Math.abs(dx) > Math.abs(dy)) {
            this.direction = dx > 0 ? Direction.Right : Direction.Left;
          } else {
            this.direction = dy > 0 ? Direction.Down : Direction.Up;
          }
        } else {
          this.isMoving = false;
          this.patrolIndex++;
          this.pauseTimer = waypoint.pauseMs;
        }
      }
      return;
    }

    switch (this.state) {
      case "IDLE":
        if (this.stateTimer > 2000 + Math.random() * 4000) {
          this.pickWanderTarget(collisionGrid);
          this.state = "WANDER";
          this.stateTimer = 0;
        }
        break;
      case "WANDER":
        if (this.wanderTarget) {
          const dx = this.wanderTarget.x - this.position.x;
          const dy = this.wanderTarget.y - this.position.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist > 5) {
            this.isMoving = true;
            const speed = 1.2;
            this.position.x += (dx / dist) * speed;
            this.position.y += (dy / dist) * speed;
            if (Math.abs(dx) > Math.abs(dy)) {
              this.direction = dx > 0 ? Direction.Right : Direction.Left;
            } else {
              this.direction = dy > 0 ? Direction.Down : Direction.Up;
            }
          } else {
            this.isMoving = false;
            this.state = "IDLE";
            this.stateTimer = 0;
            this.wanderTarget = null;
          }
        }
        break;
    }
  }

  private pickWanderTarget(collisionGrid?: boolean[][]): void {
    const maxRange = 5 * TILE_SIZE;
    const baseX = this.position.x;
    const baseY = this.position.y;
    let attempts = 0;
    while (attempts < 10) {
      const angle = Math.random() * Math.PI * 2;
      const dist = 2 * TILE_SIZE + Math.random() * maxRange;
      const tx = baseX + Math.cos(angle) * dist;
      const ty = baseY + Math.sin(angle) * dist;
      const col = Math.floor(tx / TILE_SIZE);
      const row = Math.floor(ty / TILE_SIZE);
      if (
        collisionGrid &&
        row >= 0 && row < collisionGrid.length &&
        col >= 0 && col < (collisionGrid[0]?.length ?? 0) &&
        !collisionGrid[row][col]
      ) {
        this.wanderTarget = { x: tx, y: ty };
        return;
      } else if (!collisionGrid) {
        this.wanderTarget = { x: tx, y: ty };
        return;
      }
      attempts++;
    }
  }

  /**
   * Draw the character on the canvas.
   * Uses real sprite if available, otherwise draws a placeholder.
   */
  draw(ctx: CanvasRenderingContext2D, camera: Camera, sprites: SpriteSheet): void {
    const screenX = this.position.x;
    const screenY = this.position.y;

    // Check if character sprite sheet exists
    if (sprites.hasSheet(`char_${this.spriteKey}`)) {
      // Real sprite: row = direction, col = animFrame
      sprites.drawSprite(
        ctx,
        `char_${this.spriteKey}`,
        this.animFrame,
        this.direction,
        screenX,
        screenY,
        this.width,
        this.height,
      );
    } else {
      // Placeholder character
      sprites.drawCharacterPlaceholder(
        ctx,
        this.color,
        screenX,
        screenY,
        this.direction,
        this.animFrame,
      );
    }
  }

  /**
   * Get the bounding box for collision detection.
   * Uses a smaller hitbox than the visual sprite (feet area).
   */
  getHitbox(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.position.x + 2,
      y: this.position.y + this.height - 10,
      width: this.width - 4,
      height: 10,
    };
  }

  /**
   * Get the center position of the character.
   */
  getCenter(): Position {
    return {
      x: this.position.x + this.width / 2,
      y: this.position.y + this.height / 2,
    };
  }

  /**
   * Set position directly.
   */
  setPosition(x: number, y: number): void {
    this.position.x = x;
    this.position.y = y;
  }
}
